// @givmo/board — revising the text of a consent that is already before the board.
//
// ══ WHAT A REVISION COSTS, AND WHY THE OFFICER IS TOLD BEFORE THEY START ════════════════════════
//
// A signature binds BYTES. A version's content address is computed over the instrument's text alone,
// every signature echoes that address, and the whole evidentiary value of the act is that it names the
// exact bytes a human read. So changing the text cannot carry signatures forward — there is no honest
// sense in which someone consented to words they never saw.
//
// A revision therefore:
//   * opens a NEW version with a new address, and seals the old one;
//   * takes a FRESH roster snapshot, so it goes to the board as it now stands (this is what makes the
//     "a removed seat stays required" rule tolerable — re-circulating is the sanctioned way to move
//     the required set, and it is visible and expensive rather than quiet);
//   * leaves every signature already given attached to the version it was given on, where it stays
//     readable;
//   * emails every director on the new snapshot that the revised consent is waiting for their
//     signature (CBG-OPS1-CIRC), which the dialog says before the officer starts it; and
//   * records the officer's reason, so the history says why the text moved.
//
// The dialog states the count that will be reset, in figures, before the officer types anything. A
// warning that does not name the number is one an officer skims.
//
// ⚠ A REVISION THAT CHANGES NOTHING IS REFUSED by the backend — the content address makes "no change"
// checkable, and resetting a board's signatures for no change would be a way to restart a count that
// was going badly. This dialog keeps its own submit disabled in that case too, so an officer meets the
// rule as an explanation rather than as a rejected save.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, TextField, Textarea, toast } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { consentDeliveryWords as circ } from './consent-delivery-words';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';

export interface ReviseDialogProps {
  orgRef: string;
  consentId: string;
  /** The live version's current text, which the officer edits from. */
  currentTitle: string;
  currentBody: string;
  /** How many seats have signed the version in force — what a revision will reset. */
  signedCount: number;
  /** CBG-YR6-CONSENT: a committee's consent goes back to its voting members, said so. */
  committeeName?: string | null;
  onClose: () => void;
  onRevised: () => void;
}

export function ReviseDialog({
  orgRef,
  consentId,
  currentTitle,
  currentBody,
  signedCount,
  committeeName = null,
  onClose,
  onRevised,
}: ReviseDialogProps) {
  const operations = useBoardOperations();
  const [title, setTitle] = useState(currentTitle);
  const [body, setBody] = useState(currentBody);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const unchanged = title.trim() === currentTitle.trim() && body === currentBody;

  const revise = useMutation({
    mutationFn: async () => {
      await operations.officerReviseBoardWrittenConsent({
        path: { org_ref: orgRef, consent_id: consentId },
        body: { title: title.trim(), body, reason: reason.trim() },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(
        committeeName !== null
          ? cm.newVersionBefore(committeeName)
          : 'A new version is before your board.',
      );
      onRevised();
    },
    onError: (caught) =>
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t save this revision. Please try again.',
      ),
  });

  const canSave = !unchanged && title.trim().length > 0 && reason.trim().length > 0;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !revise.isPending && onClose()}>
      <Dialog.Content
        aria-label="Change the wording"
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId="consent-revise-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Change the wording
        </Dialog.Title>

        <p
          className="rounded-md border border-warning-500 bg-warning-50 p-16 font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong"
          data-testid="consent-revise-reset-warning"
        >
          {committeeName !== null
            ? cm.reviseWarning(committeeName, signedCount)
            : signedCount === 0
              ? 'Changing the wording starts a new version and asks your board again from the beginning. It goes back out to your board as it stands today, which may not be who it went to the first time.'
              : `Changing the wording starts a new version, and ${signedCount} ${
                  signedCount === 1
                    ? 'signature already given stays'
                    : 'signatures already given stay'
                } with the old wording. Everyone has to sign again — nobody consents to words they haven’t read. It goes back out to your board as it stands today, which may not be who it went to the first time.`}
        </p>

        <TextField
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          testId="consent-revise-title"
        />

        <Textarea
          label="What the consent says"
          rows={12}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          testId="consent-revise-body"
        />

        <TextField
          label="Why the wording is changing"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          testId="consent-revise-reason"
        />
        <FieldNote>
          This is recorded with the new version, so the history says why it moved.
        </FieldNote>

        {unchanged ? (
          <FieldNote testId="consent-revise-unchanged">
            {committeeName !== null
              ? cm.reviseUnchanged
              : 'Nothing has changed yet. A new version has to differ from the one your board already has.'}
          </FieldNote>
        ) : null}

        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          data-testid="consent-revise-email"
        >
          {committeeName !== null ? cm.reviseEmails : circ.reviseEmails}
        </p>

        {error !== null ? <ErrorMessage testId="consent-revise-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={revise.isPending}
            onClick={onClose}
            testId="consent-revise-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={revise.isPending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              revise.mutate();
            }}
            testId="consent-revise-save"
          >
            Start a new version
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
